import React, { useEffect, useCallback } from 'react'
import { FilledButton } from '../../common/buttons/FilledButton'
import { SimpleButton } from '../../common/buttons/SimpleButton'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { translations } from '../../../../utils/translations/translations-manager'
import { formatAmount } from '../../../../utils/strings/amount-format'
import { useDonationAmountValidation } from '../../../../hooks/contracts/validations/DonationAmountValidationHook'
import { useSubscriptionChange } from '../../../../hooks/contracts/subscriptions/SubscriptionChangeHook'
import { ContractAmountInput } from '../inputs/ContractAmountInput'

const validationErrorMessages: Record<'same_as_before' | 'invalid', () => string> = {
  same_as_before: () => translations.texts.amount_same_as_before_error.toString(),
  invalid: () => translations.texts.invalid_amount.toString(),
}

const errorMessages: Record<'save_error', () => string> = {
  save_error: () => translations.texts.contract_update_error.toString(),
}

export function SubscriptionAmountChange({
  charityId,
  contractId,
  initialValue,
  success,
  complete,
  cancel,
}: {
  charityId: string
  contractId: string
  initialValue?: number
  success: () => void
  complete: () => void
  cancel: () => void
}): JSX.Element {
  const { validate, setAmount, amount, validationError } = useDonationAmountValidation(initialValue)
  const { save, error, clearError, state } = useSubscriptionChange(charityId, contractId)

  useEffect(() => {
    if (state === 'success') complete()
  }, [state])

  const onSave = useCallback(() => {
    if (validate()) {
      save({ selectedDonationAmount: amount })
    }
  }, [amount, save, validate])

  if (error) {
    return (
      <>
        <p className="mb-5 text-red-500">{errorMessages[error]()}</p>
        <FilledButton
          onClick={() => {
            clearError()
          }}
          label={translations.texts.retry.toString()}
        />
      </>
    )
  }

  if (state === 'success') {
    return (
      <>
        <div className="flex flex-col items-center justify-center">
          <CheckCircleIcon className="mr-2 mb-2 w-12 h-12 stroke-emerald-500" />
          <p className="mb-5">
            {translations.texts.new_amount.toString()}
            {amount && <span className="font-bold">{': ' + formatAmount(amount / 100)}</span>}
          </p>
        </div>
        <FilledButton onClick={success} label={translations.texts.close.toString()} />
      </>
    )
  }

  return (
    <>
      <ContractAmountInput value={amount} setValue={setAmount} error={validationError ? validationErrorMessages[validationError]() : undefined} />
      <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <FilledButton
          onClick={onSave}
          label={translations.texts.confirm.toString()}
          disabled={amount === undefined || validationError !== undefined}
          loading={state === 'loading'}
        />
        <SimpleButton onClick={cancel} label={translations.texts.cancel.toString()} disabled={state === 'loading'} />
      </div>
    </>
  )
}
